import { useCallback, useState } from 'react';

import ApiClient from '../config/ApiClient';
import {
  isIosNativeApp,
  requestExternalPurchaseDisclosure,
  savePendingCheckout,
  startExternalCheckout,
} from '../utils/nativeCheckoutBridge';
import { parseCheckoutError, type CheckoutErrorDisplay } from '../subscriptions/checkoutErrors';
import type { EntitlementsMap, ProductId } from '../subscriptions/types';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';

export interface ConfirmCheckoutResult {
  entitlements: EntitlementsMap;
  productId: ProductId;
  assignmentId: number;
  productName: string;
  alreadyFulfilled: boolean;
}

interface CheckoutCreateResponse {
  checkout_url: string;
  session_id: string;
}

interface CheckoutConfirmResponse {
  entitlements: EntitlementsMap;
  product_id: ProductId;
  assignment_id: number;
  product_name: string;
  already_fulfilled: boolean;
}

export function useSubscriptionCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CheckoutErrorDisplay | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const startCheckout = useCallback(
    async (params: {
      assignmentId?: number;
      productId: ProductId;
      returnTo?: string;
    }): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const body: Record<string, string | number> = {
          product_id: params.productId,
        };
        if (params.assignmentId !== undefined) {
          body.assignment_id = params.assignmentId;
        }
        if (params.returnTo) {
          body.return_to = params.returnTo;
        }

        const response = await ApiClient.post<CheckoutCreateResponse>(
          '/api/subscriptions/checkout/',
          body,
        );
        if (response.error || !response.data?.checkout_url || !response.data?.session_id) {
          setError(parseCheckoutError(response));
          return false;
        }

        if (isIosNativeApp()) {
          const approved = await requestExternalPurchaseDisclosure();
          if (!approved) {
            return false;
          }
          savePendingCheckout({
            sessionId: response.data.session_id,
            productId: params.productId,
            ...(params.assignmentId !== undefined ? { assignmentId: params.assignmentId } : {}),
          });
          await startExternalCheckout(response.data.checkout_url);
          return true;
        }

        window.location.assign(response.data.checkout_url);
        return true;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const confirmCheckout = useCallback(
    async (sessionId: string): Promise<ConfirmCheckoutResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await ApiClient.post<CheckoutConfirmResponse>(
          '/api/subscriptions/confirm/',
          { session_id: sessionId },
        );
        if (response.error || !response.data) {
          setError(parseCheckoutError(response));
          return null;
        }
        return {
          entitlements: normalizeEntitlementsMap(response.data.entitlements),
          productId: response.data.product_id,
          assignmentId: response.data.assignment_id,
          productName: response.data.product_name,
          alreadyFulfilled: response.data.already_fulfilled,
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    clearError,
    startCheckout,
    confirmCheckout,
  };
}
